/**
 * Login Page - Step 1 of OAuth flow
 * Simple page with Google OAuth button
 */
// @ts-nocheck - Temporary fix for react-icons type compatibility with Chakra UI
import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FiSun, FiShield, FiUsers } from 'react-icons/fi';
import { authService } from '../services/AuthService';
import { useAuth } from '../contexts/AuthContext';

export function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && isAuthenticated) {
      window.location.href = '/';
    }
  }, [isAuthenticated, loading]);

  const handleGoogleLogin = () => {
    // Clear any stored invite/turnstile data for clean login
    sessionStorage.removeItem('invite_code');
    sessionStorage.removeItem('turnstile_token');

    authService.initiateGoogleLogin();
  };

  // Show nothing while checking auth
  if (loading) {
    return null;
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header - matching Hora style exactly */}
      <Box
        as="header"
        h="60px"
        background="linear-gradient(45deg, rgba(255, 115, 84, 0.8) 0%, rgba(255, 184, 108, 0.6) 25%, rgba(108, 198, 255, 0.5) 50%, rgba(84, 255, 159, 0.4) 75%, rgba(0, 235, 255, 0.27) 100%)"
        backgroundSize="400% 400%"
        animation="gradientShift 108s ease-in-out infinite"
        boxShadow="0 2px 20px rgba(0,0,0,0.15)"
        px={5}
        position="sticky"
        top={0}
        zIndex={10}
        display="flex"
        alignItems="center"
      >
        <Text
          fontSize="24px"
          fontWeight="bold"
          color="rgba(255, 255, 255, 0.5)"
          fontFamily="var(--font-ui-heading)"
          letterSpacing="0.5px"
        >
          Bhrigu Hora
        </Text>
      </Box>

      <style>
        {`
          @keyframes gradientShift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
        `}
      </style>

      {/* Main Content */}
      <Container maxW="container.md" py={16}>
        <VStack spacing={8} align="stretch">
          {/* Hero Section */}
          <VStack spacing={4} textAlign="center">
            <Heading
              size="2xl"
              bgGradient="linear(to-r, orange.400, red.500, purple.500)"
              bgClip="text"
              lineHeight="1.4"
            >
              Welcome to Bhrigu Hora
            </Heading>
            <Text fontSize="xl" color="gray.600">
              Authentic Vedic Astrology powered by Swiss Ephemeris
            </Text>
            <HStack spacing={8} pt={4}>
              <VStack spacing={1}>
                <Icon as={FiSun} w={6} h={6} color="orange.500" />
                <Text fontSize="sm" color="gray.600">100% Vedic</Text>
              </VStack>
              <VStack spacing={1}>
                <Icon as={FiShield} w={6} h={6} color="blue.500" />
                <Text fontSize="sm" color="gray.600">Secure</Text>
              </VStack>
              <VStack spacing={1}>
                <Icon as={FiUsers} w={6} h={6} color="purple.500" />
                <Text fontSize="sm" color="gray.600">Invite-only</Text>
              </VStack>
            </HStack>
          </VStack>

          <Divider />

          {/* Login Card */}
          <Box
            bg="white"
            p={8}
            borderRadius="xl"
            boxShadow="xl"
            border="1px"
            borderColor="gray.200"
          >
            <VStack spacing={6} align="stretch">
              <Heading size="md" textAlign="center">
                Sign in to Continue
              </Heading>

              <Text fontSize="sm" color="gray.600" textAlign="center">
                Click below to sign in with your Google account
              </Text>

              {/* Google Login Button */}
              <Button
                onClick={handleGoogleLogin}
                size="lg"
                width="full"
                colorScheme="blue"
                leftIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                  </svg>
                }
              >
                Continue with Google
              </Button>
            </VStack>
          </Box>

          {/* Footer Info */}
          <VStack spacing={2} pt={8}>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Bhrigu Hora uses invite-only registration to maintain quality and security
            </Text>
            <Text fontSize="xs" color="gray.500" textAlign="center">
              Don't have an invite? Ask a friend who's already using Bhrigu Hora
            </Text>
            <Divider pt={4} />
            <Text fontSize="sm" color="gray.600" textAlign="center" pt={4}>
              Need to register?{' '}
              <Button
                as={Link}
                to="/"
                variant="link"
                colorScheme="blue"
                fontWeight="semibold"
              >
                Sign up here
              </Button>
            </Text>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
}
